import React, { useEffect } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useFormContext } from 'react-hook-form'
import useOrgFilter from '../hooks/useOrgFilter'

const useStyles = makeStyles(() => ({
  asterisk: { color: '#f44336' },
  autocomplete: {
    '& .MuiFilledInput-root .MuiAutocomplete-input': {
      padding: '10px 4px 10px 6px'
    },
    '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
      display: 'none'
    }
  }
}))

// Dual-mode "Filing For" org picker for parent-corp AND admin users — mirrors SearchableCountryField's
// controlled-vs-RHF-bound pattern. Renders nothing for plain org users (nothing to act on behalf of).
function OrgActingSelector({ name, value: valueProp, onChange, disabled, required, error, helperText, label = 'Filing For', size = 'small' }) {
  const classes = useStyles()
  const formContext = useFormContext()
  const { canActAsOtherOrg, orgList, loggedInOrgId } = useOrgFilter()
  const isControlled = valueProp !== undefined
  const currentValue = isControlled ? (valueProp ?? null) : (name ? (formContext?.watch(name) ?? null) : null)

  const selectedOption = orgList.find((o) => o.id === currentValue) || null

  const fieldError = !isControlled && name ? formContext?.formState?.errors?.[name] : undefined

  // Default to the current logged-in party once, only when nothing is set yet — leaves an
  // Edit/Clone page's prefilled value alone. Uses loggedInOrgId (not effectiveOrgId) so this
  // defaults for every role, including admin, whose effectiveOrgId is null until they pick.
  useEffect(() => {
    if (!canActAsOtherOrg || currentValue != null || loggedInOrgId == null) return
    if (isControlled) {
      onChange && onChange({ target: { value: loggedInOrgId } })
    } else if (name && formContext) {
      formContext.setValue(name, loggedInOrgId, { shouldValidate: true })
    }
  }, [canActAsOtherOrg, currentValue, loggedInOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!canActAsOtherOrg) return null

  function handleChange(_, option) {
    const newVal = option ? option.id : null
    if (isControlled) {
      onChange && onChange({ target: { value: newVal } })
    } else if (name && formContext) {
      formContext.setValue(name, newVal, { shouldValidate: true })
    }
  }

  return (
    <Autocomplete
      className={classes.autocomplete}
      style={{ width: '100%' }}
      options={orgList}
      getOptionLabel={(o) => o.name ?? ''}
      getOptionSelected={(opt, val) => opt?.id === val?.id}
      value={selectedOption}
      onChange={handleChange}
      disabled={disabled}
      noOptionsText='No organisations found'
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          required={required}
          helperText={fieldError?.message || helperText}
          error={!!fieldError || error}
          size={size}
          variant='filled'
          InputLabelProps={{
            ...params.InputLabelProps,
            classes: {
              root: 'ng-form-control-label',
              focused: 'ng-form-control-label-filled-focused',
              error: 'ng-form-control-label-error',
              asterisk: classes.asterisk
            }
          }}
          InputProps={{
            ...params.InputProps,
            classes: {
              root: `ng-form-control${label ? '' : ' no-label'}`,
              underline: 'ng-form-control-filled',
              focused: 'ng-form-control-filled-focused',
              error: 'ng-form-control-filled-error'
            },
            style: { backgroundColor: '#ffffff', border: '1px solid #5f5753', borderRadius: 4 },
            disableUnderline: true
          }}
        />
      )}
    />
  )
}

export default OrgActingSelector
